import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { gsap } from "@/lib/gsap";
import { useT } from "@/i18n";
import { PRODUCTS_LIST } from "@/data/products";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useT();
  const product = PRODUCTS_LIST.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (product) {
      document.title = `${product.name} — Makario`;
      const desc = product.tagline[lang] || product.tagline.en;
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) {
        meta = document.createElement("meta");
        meta.name = "description";
        document.head.appendChild(meta);
      }
      meta.content = `${product.name} — ${desc}`;
    }
  }, [product, lang]);

  useEffect(() => {
    if (!product) return;
    const ctx = gsap.context(() => {
      gsap.from("[data-anim='pd-title']", { opacity: 0, y: 50, duration: 1.05, ease: "power3.out", delay: 0.05 });
      gsap.from("[data-anim='pd-tag']", { opacity: 0, y: 30, duration: 0.9, delay: 0.25 });
      gsap.from("[data-anim='pd-mark']", { opacity: 0, scale: 0.94, duration: 1.4, ease: "power3.out" });
      gsap.from("[data-anim='pd-body-row']", {
        opacity: 0, y: 40, duration: 0.85, stagger: 0.12,
        scrollTrigger: { trigger: "[data-anim='pd-body']", start: "top 80%" },
      });
    });
    return () => ctx.revert();
  }, [product]);

  if (!product) {
    return (
      <main className="min-h-screen flex items-center justify-center text-[var(--ink)]">
        <div className="text-center">
          <p className="h-sub">{t("detail.notFound")}</p>
          <button onClick={() => navigate("/")} className="btn-pill mt-8 text-[var(--ink)] border-[var(--ink)]">
            <ArrowLeft size={14} /> {t("detail.back")}
          </button>
        </div>
      </main>
    );
  }

  const isLightAccent = product.accent === "#d8ff5c" || product.accent === "#b8e637";
  const productIdx = PRODUCTS_LIST.findIndex((p) => p.slug === product.slug);

  return (
    <main data-testid="product-detail" className="normal-scroll">
      <Navbar />

      {/* HERO — editorial */}
      <section className="relative min-h-screen flex flex-col px-6 sm:px-8 md:px-16 lg:px-24 pt-36 md:pt-32 pb-16 overflow-hidden grain" style={{ background: "var(--ink)" }}>
        <div
          className="absolute pointer-events-none rounded-full"
          style={{
            width: 950, height: 950, top: "-22%", right: "-15%",
            background: `radial-gradient(circle, ${product.accent} 0%, transparent 62%)`,
            opacity: 0.18, filter: "blur(40px)",
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.05]"
          style={{
            backgroundImage: "linear-gradient(90deg, transparent calc(100% - 1px), white calc(100% - 1px))",
            backgroundSize: "12.5% 100%",
          }}
        />

        {/* Top eyebrow row */}
        <div className="relative z-10 flex items-center justify-between mb-12 md:mb-16">
          <Link
            to="/#products"
            data-testid="pd-back"
            className="inline-flex items-center gap-2 text-white/65 hover:text-white text-[0.65rem] font-mono uppercase tracking-[0.28em]"
          >
            <ArrowLeft size={14} /> {t("detail.back")}
          </Link>
          <div className="flex items-center gap-6">
            <span className="font-mono text-[0.62rem] uppercase tracking-[0.3em] text-white/45">
              {String(productIdx + 1).padStart(2, "0")} / {String(PRODUCTS_LIST.length).padStart(2, "0")}
            </span>
            <span className="font-mono text-[0.62rem] uppercase tracking-[0.3em] text-white/45">
              {t("nav.products")}
            </span>
          </div>
        </div>

        {/* Main editorial grid */}
        <div className="relative z-10 flex-1 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7 text-white">
            <div className="flex items-center gap-3 mb-7">
              <span
                className="font-mono text-[0.6rem] uppercase tracking-[0.28em] px-2.5 py-1 rounded-sm"
                style={{ background: product.accent, color: isLightAccent ? "#0c0e14" : "#fff" }}
              >
                {product.code}
              </span>
              {product.fullName && (
                <span className="font-mono text-[0.65rem] uppercase tracking-[0.28em] text-white/55">
                  {product.fullName}
                </span>
              )}
            </div>
            <h1
              data-anim="pd-title"
              data-testid="pd-title"
              className="font-display text-white"
              style={{
                fontSize: "clamp(3rem, 7vw, 6.4rem)",
                lineHeight: 0.92,
                letterSpacing: "-0.035em",
                fontWeight: 700,
              }}
            >
              {product.name}
            </h1>
            <p
              data-anim="pd-tag"
              className="mt-10 max-w-[34rem] font-display"
              style={{
                fontSize: "clamp(1.05rem, 1.6vw, 1.4rem)",
                lineHeight: 1.4,
                color: product.accent,
                fontWeight: 500,
              }}
            >
              {product.tagline[lang]}
            </p>
            <p className="mt-8 max-w-[40rem] text-white/65 text-[15px] leading-relaxed">
              {product.intro[lang]}
            </p>
          </div>

          {/* Watermark code */}
          <div className="lg:col-span-5 hidden lg:flex items-center justify-center">
            <div
              data-anim="pd-mark"
              className="font-display select-none leading-none"
              style={{
                fontSize: "clamp(8rem, 22vw, 22rem)",
                fontWeight: 800,
                letterSpacing: "-0.06em",
                color: product.accent,
                opacity: 0.16,
                textShadow: `0 0 80px ${product.accent}40`,
              }}
            >
              {product.code}
            </div>
          </div>
        </div>

        {/* Bottom rule */}
        <div className="relative z-10 mt-12 pt-6 border-t border-white/10 flex items-center justify-between font-mono text-[0.6rem] uppercase tracking-[0.28em] text-white/35">
          <span>{lang === "id" ? "Produk Makario" : "A Makario product"}</span>
          <span>{product.code} · v1</span>
        </div>
      </section>

      {/* BODY — spec sheet */}
      <section
        data-anim="pd-body"
        className="relative px-6 sm:px-8 md:px-16 lg:px-24 py-24 md:py-36"
        style={{ background: "var(--paper)" }}
      >
        <div className="max-w-[1240px] mx-auto space-y-24">
          {/* Capabilities */}
          <div data-anim="pd-body-row" className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="font-mono text-[0.6rem] uppercase tracking-[0.32em] text-black/40 mb-3">
                /01 · {lang === "id" ? "Kapabilitas" : "Capabilities"}
              </div>
              <h2 className="font-display text-[var(--ink)]" style={{ fontSize: "clamp(1.6rem,2.6vw,2.2rem)", letterSpacing: "-0.02em", fontWeight: 600, lineHeight: 1.1 }}>
                {lang === "id" ? "Apa yang dikerjakan." : "What it does."}
              </h2>
            </div>
            <ul className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-0 border-t border-black/10">
              {product.capabilities[lang].map((c, i) => (
                <li key={c} className="flex items-baseline gap-4 py-4 border-b border-black/10">
                  <span className="font-mono text-[0.6rem] tracking-[0.24em] text-black/40 flex-none">
                    /{String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-[15px] leading-snug text-black/75">{c}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Best for */}
          <div data-anim="pd-body-row" className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="font-mono text-[0.6rem] uppercase tracking-[0.32em] text-black/40 mb-3">
                /02 · {lang === "id" ? "Cocok untuk" : "Best for"}
              </div>
              <h2 className="font-display text-[var(--ink)]" style={{ fontSize: "clamp(1.6rem,2.6vw,2.2rem)", letterSpacing: "-0.02em", fontWeight: 600, lineHeight: 1.1 }}>
                {lang === "id" ? "Siapa yang memakainya." : "Who runs it."}
              </h2>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-x-10">
              {product.bestFor[lang].map((b, i) => (
                <div key={b} className={`py-5 border-t border-black/10 ${i >= product.bestFor[lang].length - 2 ? "" : "sm:border-b-0"}`}>
                  <div className="text-[15px] leading-snug text-black/80 flex items-baseline gap-3">
                    <span
                      className="inline-block w-1.5 h-1.5 rounded-full flex-none translate-y-[2px]"
                      style={{ background: product.accent }}
                    />
                    {b}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA strip */}
          <div data-anim="pd-body-row" className="relative rounded-2xl overflow-hidden p-10 md:p-14" style={{ background: "var(--ink)" }}>
            <div
              className="absolute pointer-events-none rounded-full"
              style={{
                width: 480, height: 480, top: "-40%", right: "-10%",
                background: `radial-gradient(circle, ${product.accent} 0%, transparent 60%)`,
                opacity: 0.22, filter: "blur(30px)",
              }}
            />
            <div className="relative flex flex-col md:flex-row gap-8 md:items-end md:justify-between">
              <div className="text-white max-w-[34rem]">
                <div className="font-mono text-[0.6rem] uppercase tracking-[0.32em] text-white/45 mb-3">
                  /03 · {lang === "id" ? "Mulai" : "Start"}
                </div>
                <p className="font-display text-white" style={{ fontSize: "clamp(1.4rem,2.2vw,1.9rem)", letterSpacing: "-0.02em", fontWeight: 500, lineHeight: 1.2 }}>
                  {lang === "id"
                    ? `Diskusikan implementasi ${product.name} untuk organisasi Anda.`
                    : `Discuss a ${product.name} implementation for your organisation.`}
                </p>
              </div>
              <Link
                to="/#contact"
                className="inline-flex items-center gap-3 self-start md:self-auto font-mono text-[0.65rem] uppercase tracking-[0.28em] text-white border border-white/30 rounded-full px-5 py-3 hover:bg-white hover:text-black transition-all duration-300"
              >
                {t("nav.cta")} <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>

          {/* Other products */}
          <div data-anim="pd-body-row" className="pt-10">
            <div className="font-mono text-[0.6rem] uppercase tracking-[0.32em] text-black/40 mb-8">
              /04 · {lang === "id" ? "Produk Lainnya" : "Other Products"}
            </div>
            <ol className="border-t border-black/10">
              {PRODUCTS_LIST.filter((p) => p.slug !== product.slug).map((p) => (
                <li key={p.slug} className="border-b border-black/10">
                  <Link
                    to={`/products/${p.slug}`}
                    data-testid={`pd-related-${p.slug}`}
                    className="group flex items-center justify-between gap-6 py-6"
                  >
                    <div className="flex items-baseline gap-6 min-w-0">
                      <span
                        className="font-mono text-[0.6rem] uppercase tracking-[0.28em] flex-none"
                        style={{ color: p.accent }}
                      >
                        {p.code}
                      </span>
                      <span
                        className="font-display text-[var(--ink)] truncate transition-all duration-300 group-hover:translate-x-1"
                        style={{ fontSize: "clamp(1.4rem,2.3vw,2rem)", letterSpacing: "-0.02em", fontWeight: 600 }}
                      >
                        {p.name}
                      </span>
                      <span className="hidden md:inline text-sm text-black/45 truncate">
                        — {p.tagline[lang]}
                      </span>
                    </div>
                    <ArrowUpRight size={18} className="text-black/35 group-hover:text-[var(--violet)] transition-colors flex-none" />
                  </Link>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
